import React from 'react';
import { FaCamera, FaTrashAlt, FaCheck, FaTimes, FaSort, FaUndo } from 'react-icons/fa';
import { useVideoEditImagesSection } from './useVideoEditImagesSection';
import { VideoEditImagesSectionProps, VideoImage } from './types';
import styles from './VideoEditImagesSection.module.css';
import { IMAGE_UPLOAD_ACCEPT } from '../../utils/imageFormat';

/**
 * 動画編集ページの画像セクションコンポーネント
 * 動画に関連する画像の管理（表示、追加、削除、並べ替え）を行う
 */
const VideoEditImagesSection: React.FC<VideoEditImagesSectionProps> = (props) => {
  const { isVideoProcessing, toggleDeletionStatus, isPendingDeletion, removeImage } = props;

  const {
    isDragging,
    fileInputRef,
    isMobile,
    orderedImages,
    isReorderMode,
    selectedImages,
    isProcessing,
    isUploading,
    tempImages,
    tempImageIds,
    pendingDeletions,
    MAX_UPLOAD_COUNT,
    activeImageCount,
    getImageUrl,
    handleMultipleFiles,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileButtonClick,
    handleImageDragStart,
    handleImageDragOver,
    handleImageDragLeave,
    handleImageDragEnd,
    handleImageDrop,
    toggleReorderMode,
    handleImageSelect,
    applySelectedOrder,
    cancelReorderMode,
    getSelectionStatus,
    handleTempImageDelete,
    commitTempImages,
    commitPendingDeletions,
    cleanupTempImages,
    handleImageFileChange,
    setLocalOrderedImages
  } = useVideoEditImagesSection(props);

  // 処理中の動画の場合は無効化された状態を表示
  if (isVideoProcessing) {
    return (
      <div className={`${styles['video-edit-images-section']} ${styles['video-edit-images-section--disabled']}`} data-testid="video-edit-images-section">
        <div className={styles['video-edit-images-section__processing-notice']}>
          <div className={styles['video-edit-images-section__processing-icon']}>
            <FaCamera />
          </div>
          <div className={styles['video-edit-images-section__processing-text']}>
            <p className={styles['video-edit-images-section__processing-title']}>術前後画像は変換完了後に編集できます</p>
            <p className={styles['video-edit-images-section__processing-description']}>
              動画の変換処理が完了すると、術前後画像の追加・編集・並べ替えが可能になります。
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  
  // isPendingDeletionが未定義の場合のデフォルト関数
  const checkIsPendingDeletion = (imageId: string) => {
    return isPendingDeletion ? isPendingDeletion(imageId) : false;
  };

  // 保存予定の画像を直接削除する関数
  const handleTempImageDirectDelete = async (imageId: string) => {
    try {
      // removeImageを使用して即座にリストから削除
      if (removeImage) {
        removeImage(imageId);
        
        // 順序入れ替え後の状態（localOrderedImages）からも削除
        setLocalOrderedImages(prev => prev.filter(img => img.id !== imageId));
      } else {
        throw new Error('削除処理が利用できません');
      }
    } catch (error) {
      console.error('保存予定画像の削除エラー:', error);
      // エラーメッセージは親コンポーネントで表示される
    }
  };

  // PCとモバイルで異なるレイアウトをレンダリング
  const renderImageItems = () => {
    if (!orderedImages || orderedImages.length === 0) {
      return null;
    }

    if (isMobile) {
      // モバイル用リストレイアウト
      return orderedImages.map((image, index) => {
        const selectionStatus = getSelectionStatus(image.id);
        
        const isPendingDeletion = pendingDeletions.includes(image.id);
        const isTempImage = tempImageIds.has(image.id);
        
        return (
          <div 
            key={image.id} 
            className={`${styles['video-edit-images-section__image-item']} ${isReorderMode ? styles['reorder-mode'] : ''} ${selectionStatus ? styles['selected'] : ''} ${checkIsPendingDeletion(image.id) ? styles['pending-deletion'] : ''} ${isTempImage ? styles['temp-image'] : ''}`}
            data-image-id={image.id}
            onClick={isReorderMode ? () => handleImageSelect(image) : undefined}
            data-testid={`video-edit-images-section__image-item-${index}`}
          >
            <img 
              src={getImageUrl(image)} 
              alt={`動画画像 ${image.id}`}
              className={styles['video-edit-images-section__image']}
            />
            
            {!isReorderMode && (
              <div className={styles['video-edit-images-section__image-number']}>{index + 1}</div>
            )}
            
            {/* 状態ラベル */}
            {!isReorderMode && (
              <div className={styles['video-edit-images-section__status-label']}>
                {isPendingDeletion ? (
                  <div className={`${styles['video-edit-images-section__status-badge']} ${styles['pending-deletion-badge']}`}>
                    <span>削除予定</span>
                  </div>
                ) : isTempImage ? (
                  <div className={`${styles['video-edit-images-section__status-badge']} ${styles['temp-image-badge']}`}>
                    <span>保存予定</span>
                  </div>
                ) : null}
              </div>
            )}
            
            {!isReorderMode && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isTempImage) {
                    // 保存予定の画像は直接削除
                    handleTempImageDirectDelete(image.id);
                  } else {
                    // 既存画像は削除予定に設定
                    toggleDeletionStatus?.(image.id);
                  }
                }}
                className={styles['video-edit-images-section__image-delete-button']}
                aria-label={checkIsPendingDeletion(image.id) ? "削除予定をキャンセル" : "画像を削除"}
                data-testid={`video-edit-images-section__delete-button-${index}`}
              >
                {checkIsPendingDeletion(image.id) ? <FaUndo /> : <FaTrashAlt />}
              </button>
            )}
            
            {isReorderMode && (
              <div className={`${styles['video-edit-images-section__selection-indicator']} ${selectionStatus ? styles['selected'] : ''}`}>
                {selectionStatus ? (
                  <div className={styles['video-edit-images-section__image-number']}>{selectionStatus}</div>
                ) : (
                  <div className={styles['video-edit-images-section__select-prompt']}>タップして選択</div>
                )}
              </div>
            )}
          </div>
        );
      });
    } else {
      // PC用グリッドレイアウト
      return orderedImages.map((image, index) => {
        const isPendingDeletion = pendingDeletions.includes(image.id);
        const isTempImage = tempImageIds.has(image.id);
        
        return (
          <div 
            key={image.id} 
            className={`${styles['video-edit-images-section__image-item']} ${checkIsPendingDeletion(image.id) ? styles['pending-deletion'] : ''} ${isTempImage ? styles['temp-image'] : ''}`}
            draggable={true}
            onDragStart={(e) => handleImageDragStart(e, index)}
            onDragOver={handleImageDragOver}
            onDragLeave={handleImageDragLeave}
            onDragEnd={handleImageDragEnd}
            onDrop={(e) => handleImageDrop(e, index)}
            data-image-id={image.id}
            data-testid={`video-edit-images-section__image-item-${index}`}
          >
          <div className={styles['video-edit-images-section__image-number']}>{index + 1}</div>
          
          <img 
            src={getImageUrl(image)} 
            alt={`動画画像 ${image.id}`}
            className={styles['video-edit-images-section__image']}
          />
          
          {/* 状態ラベル */}
          <div className={styles['video-edit-images-section__status-label']}>
            {isPendingDeletion ? (
              <div className={`${styles['video-edit-images-section__status-badge']} ${styles['pending-deletion-badge']}`}>
                <span>削除予定</span>
              </div>
            ) : isTempImage ? (
              <div className={`${styles['video-edit-images-section__status-badge']} ${styles['temp-image-badge']}`}>
                <span>保存予定</span>
              </div>
            ) : null}
          </div>
          
          <button
            type="button"
            onClick={() => {
              if (isTempImage) {
                // 保存予定の画像は直接削除
                handleTempImageDirectDelete(image.id);
              } else {
                // 既存画像は削除予定に設定
                toggleDeletionStatus?.(image.id);
              }
            }}
            className={`${styles['video-edit-images-section__image-delete-button']} ${checkIsPendingDeletion(image.id) ? styles['restore-button'] : styles['delete-button']}`}
            aria-label={checkIsPendingDeletion(image.id) ? "削除予定をキャンセル" : "画像を削除"}
            data-testid={`video-edit-images-section__delete-button-${index}`}
          >
            {checkIsPendingDeletion(image.id) ? <FaUndo /> : <FaTrashAlt />}
            {checkIsPendingDeletion(image.id) && (
              <span className={styles['button-text']}>
                復元
              </span>
            )}
          </button>
        </div>
        );
      });
    }
  };

  return (
    <div className={styles['video-edit-images-section']} data-testid="video-edit-images-section">
      {/* セクションヘッダーは画像が2枚以上ある場合のみ表示 */}
      {isMobile && orderedImages.length > 1 && (
        <div className={styles['video-edit-images-section__section-header']}>
          <div className={styles['video-edit-images-section__image-actions']}>
            {isReorderMode ? (
              <>
                <button 
                  type="button"
                  className={`${styles['video-edit-images-section__action-button']} ${styles['video-edit-images-section__cancel-button']}`}
                  onClick={cancelReorderMode}
                  aria-label="並べ替えをキャンセル"
                  data-testid="video-edit-images-section__cancel-button"
                >
                  <FaTimes />
                  <span>キャンセル</span>
                </button>
                
                <button 
                  type="button"
                  className={`${styles['video-edit-images-section__action-button']} ${styles['video-edit-images-section__done-button']}`}
                  onClick={applySelectedOrder}
                  aria-label="並べ替えを適用"
                  disabled={selectedImages.length !== orderedImages.length || isProcessing}
                  data-testid="video-edit-images-section__apply-button"
                >
                  <FaCheck />
                  <span>適用</span>
                </button>
              </>
            ) : (
              <button 
                type="button"
                className={`${styles['video-edit-images-section__action-button']} ${styles['video-edit-images-section__reorder-button']}`}
                onClick={toggleReorderMode}
                aria-label="画像を並べ替え"
                data-testid="video-edit-images-section__reorder-button"
              >
                <FaSort />
                <span>並べ替え</span>
              </button>
            )}
          </div>
        </div>
      )}
      
      {isReorderMode && isMobile && (
        <div className={styles['video-edit-images-section__reorder-instructions']} data-testid="video-edit-images-section__reorder-instructions">
          {selectedImages.length === 0 ? (
            "表示したい順番に画像をタップしてください"
          ) : selectedImages.length < orderedImages.length ? (
            `${selectedImages.length}/${orderedImages.length}枚選択済み - 残りの画像も選択してください`
          ) : (
            "すべての画像を選択しました - 「適用」をタップして確定"
          )}
        </div>
      )}
      
      {orderedImages && orderedImages.length > 0 ? (
        <div 
          className={isMobile ? styles['video-edit-images-section__image-list'] : styles['video-edit-images-section__image-grid']}
          data-testid="video-edit-images-section__images-container"
        >
          {renderImageItems()}
        </div>
      ) : (
        <div className={styles['video-edit-images-section__no-images-message']} data-testid="video-edit-images-section__no-images">
          <p>この動画に関連する画像はまだ登録されていません。</p>
          <p>画像を追加すると、動画の内容をより分かりやすく伝えることができます。</p>
        </div>
      )}
      
      {/* 削除予定を除いた既存画像・保存予定の画像が最大数に達していない場合のみドロップゾーンを表示 */}
      {activeImageCount < MAX_UPLOAD_COUNT && (
        <div 
          className={`${styles['video-edit-images-section__image-dropzone']} ${isDragging ? styles['dragging'] : ''} ${isUploading ? styles['uploading'] : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={isUploading ? undefined : handleFileButtonClick}
          data-testid="video-edit-images-section__dropzone"
        >
          <FaCamera className={styles['video-edit-images-section__dropzone-icon']} />
          <p className={styles['video-edit-images-section__dropzone-text']}>
            {isUploading ? (
              <>
                画像をアップロード中...
                <br />
                <small>しばらくお待ちください</small>
              </>
            ) : (
              <>
                クリックまたはドラッグ&ドロップで画像をアップロード
                <br />
                <small>削除予定を除いて最大{MAX_UPLOAD_COUNT}枚まで選択可能（保存時に正式に紐付けされます）</small>
              </>
            )}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleImageFileChange(e);
                e.target.value = '';
              }
            }}
            accept={IMAGE_UPLOAD_ACCEPT}
            multiple
            className={styles['video-edit-images-section__file-input']}
            aria-label="画像ファイルを選択"
            data-testid="video-edit-images-section__file-input"
          />
        </div>
      )}
    </div>
  );
};

export default VideoEditImagesSection; 
